import {
  Payment as PaymentIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
  AccessTime as AccessTimeIcon,
  LocalOffer as LocalOfferIcon,
  CreditCard as CreditCardIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Delete as DeleteIcon
} from "@mui/icons-material";
import {
  Box,
  IconButton,
  Tooltip,
  useTheme,
  Typography,
  Paper,
  Chip,
  Button,
  alpha,
  LinearProgress,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  useMediaQuery,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import { adminDataGridBoxSx, adminPageSx } from "../../theme/adminLayout";
import { API_BASE_URL } from "../../config/apiBase";

const PaymentTable = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const baseColors = tokens(theme.palette.mode);
  const colors = {
    ...baseColors,
    blueAccent: {
      100: "#d9eefb",
      200: "#b3ddf7",
      300: "#8dcbf4",
      400: "#67baf0",
      500: "#22a3f1",
      600: "#1b82c1",
      700: "#146291",
      800: "#0e4160",
      900: "#072030",
    },
    primary: {
      100: "#ffffff",
      200: "#fafafa",
      300: "#f5f5f5",
      400: "#ffffff",
      500: "#ffffff",
      600: "#e6e6e6",
      700: "#cccccc",
      800: "#b3b3b3",
      900: "#999999",
    },
  };

  const navigate = useNavigate();
  const token = localStorage.getItem("accessToken");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    paid: 0,
    succeeded: 0,
    failed: 0
  });

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/bookings/getAllBookingPayments`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch payments");
      }
      const responseData = await response.json();

      if (!responseData.message || !Array.isArray(responseData.data)) {
        throw new Error("Invalid response data format");
      }

      // Filter out error responses and map to our format
      const validPayments = responseData.data.filter(item => item.bookingId);
      const mappedData = validPayments.map((payment, index) => ({
        id: payment.bookingId,
        paymentId: `PAY-${payment.bookingId.toString().padStart(6, '0')}`,
        bookingId: payment.bookingId,
        amount: payment.amountTotal,
        status: payment.paymentStatus.toLowerCase(), // Use actual status from API
        paymentMethod: payment.paymentMethod || "card",
        transactionId: payment.paymentIntentId,
        renterId: payment.renterId,
        currency: payment.currency || "USD",
        processingFee: 0, // API doesn't provide this
        description: "Vehicle rental payment",
        renterName: payment.renterName,
        renterEmail: payment.renterEmail,
        sellerName: payment.sellerName,
        sellerEmail: payment.sellerEmail,
        vehicleId: payment.vehicleId,
        sellerId: payment.sellerId
      }));

      const sortedData = mappedData.sort((a, b) => b.bookingId - a.bookingId);
      setData(sortedData);

      // Calculate statistics based on actual status values
      const totalPayments = sortedData.length;
      const paidPayments = sortedData.filter(payment => payment.status === 'paid').length;
      const succeededPayments = sortedData.filter(payment => payment.status === 'succeeded').length;
      const failedPayments = sortedData.filter(payment => payment.status === 'failed').length;

      setStats({
        total: totalPayments,
        paid: paidPayments,
        succeeded: succeededPayments,
        failed: failedPayments
      });

    } catch (error) {
      console.error("Error fetching payments:", error);
      Swal.fire("Error!", "Failed to fetch payments.", "error");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  const handleDeletePayment = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this payment record!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: colors.redAccent[500],
      cancelButtonColor: colors.grey[500],
      confirmButtonText: "Delete",
      background: "#ffffff",
      color: "#333333",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await fetch(
            `${API_BASE_URL}/bookings/deletePayment/${id}`,
            {
              method: "DELETE",
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          if (!response.ok) {
            throw new Error("Failed to delete payment");
          }

          const updatedData = data.filter((item) => item.id !== id);
          setData(updatedData);

          // Update stats after deletion
          const deletedPayment = data.find(item => item.id === id);
          setStats(prev => ({
            total: prev.total - 1,
            paid: deletedPayment.status === 'paid' ? prev.paid - 1 : prev.paid,
            succeeded: deletedPayment.status === 'succeeded' ? prev.succeeded - 1 : prev.succeeded,
            failed: deletedPayment.status === 'failed' ? prev.failed - 1 : prev.failed
          }));

          Swal.fire({
            title: "Deleted!",
            text: "The payment record has been deleted.",
            icon: "success",
            background: "#ffffff",
            color: "#333333",
          });
        } catch (error) {
          console.error("Error deleting payment:", error);
          Swal.fire("Error!", "Failed to delete payment record.", "error");
        }
      }
    });
  };

  const handleViewClick = (payment) => {
    setSelectedPayment(payment);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedPayment(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return {
          bg: alpha("#4CAF50", 0.2),
          color: "#2E7D32",
          label: "Paid"
        };
      case 'succeeded':
        return {
          bg: alpha("#2196F3", 0.2),
          color: "#1565C0",
          label: "Succeeded"
        };
      case 'failed':
        return {
          bg: alpha("#F44336", 0.2),
          color: "#C62828",
          label: "Failed"
        };
      default:
        return {
          bg: alpha("#757575", 0.2),
          color: "#616161",
          label: status
        };
    }
  };

  const columns = [
    { 
      field: "paymentId", 
      headerName: "Payment ID", 
      flex: 0.8,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold" color="#333333">
          {params.row.paymentId}
        </Typography>
      )
    },
    {
      field: "paymentInfo",
      headerName: "Payment Details",
      flex: 1.8,
      renderCell: (params) => (
        <Box>
          <Typography variant="body1" fontWeight="bold" fontSize="14px" color="#333333">
            {`Booking #${params.row.bookingId}`}
          </Typography>
          <Typography variant="caption" color="#666666">
            {`${params.row.paymentMethod} - ${params.row.transactionId || 'No Transaction ID'}`}
          </Typography>
          <Typography variant="caption" color="#666666" display="block">
            {`Renter: ${params.row.renterName}`}
          </Typography>
          <Typography variant="caption" color="#666666">
            {`Seller: ${params.row.sellerName}`}
          </Typography>
        </Box>
      )
    },
    {
      field: "amount",
      headerName: "Amount",
      flex: 0.9,
      renderCell: (params) => (
        <Chip
          icon={<LocalOfferIcon />}
          label={formatCurrency(params.row.amount, params.row.currency)}
          size="small"
          sx={{ 
            fontWeight: "bold",
            borderRadius: "4px",
            backgroundColor: alpha(colors.blueAccent[500], 0.2),
            color: colors.blueAccent[500],
            '& .MuiChip-icon': {
              color: 'inherit'
            }
          }}
        />
      )
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.9,
      renderCell: (params) => {
        const statusColors = getStatusColor(params.row.status);
        return (
          <Chip
            icon={<CheckCircleIcon />}
            label={statusColors.label}
            size="small"
            sx={{ 
              fontWeight: "bold",
              borderRadius: "4px",
              backgroundColor: statusColors.bg,
              color: statusColors.color,
              '& .MuiChip-icon': {
                color: 'inherit'
              }
            }}
          />
        );
      }
    },
    { 
      field: "paymentMethod", 
      headerName: "Method", 
      flex: 0.9,
      renderCell: (params) => (
        <Chip
          icon={<CreditCardIcon />}
          label={params.row.paymentMethod}
          size="small"
          sx={{ 
            fontWeight: "bold",
            borderRadius: "4px",
            backgroundColor: alpha(colors.blueAccent[500], 0.2),
            color: colors.blueAccent[500],
            '& .MuiChip-icon': {
              color: 'inherit'
            }
          }}
        />
      )
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.6,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton 
              onClick={() => handleViewClick(params.row)}
              sx={{
                backgroundColor: alpha(colors.blueAccent[500], 0.1),
                borderRadius: "4px",
                mr: 1,
                padding: "4px",
                '&:hover': {
                  backgroundColor: alpha(colors.blueAccent[500], 0.2),
                }
              }}
            >
              <VisibilityIcon
                sx={{ color: colors.blueAccent[500], fontSize: "18px" }}
              />
            </IconButton>
          </Tooltip>
          
        </Box>
      ),
    },
  ];

  // Custom toolbar component
  const CustomToolbar = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '6px 16px',
          borderBottom: `1px solid #e0e0e0`,
        }}
      >
        <Box display="flex" alignItems="center">
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            size="small"
            sx={{
              borderColor: alpha(colors.blueAccent[500], 0.5),
              color: colors.blueAccent[500],
              mr: 2,
              '&:hover': {
                borderColor: colors.blueAccent[500],
                backgroundColor: alpha(colors.blueAccent[500], 0.1),
              }
            }}
          >
            Filter
          </Button>
          <GridToolbar 
            sx={{
              '& .MuiButton-root': {
                color: "#757575",
                '&:hover': {
                  backgroundColor: alpha("#e0e0e0", 0.5),
                }
              }
            }}
          />
        </Box>
      </Box>
    );
  };

  // Payment Details Dialog
  const PaymentDetailsDialog = ({ payment, open, onClose }) => {
    if (!payment) return null;

    const statusColors = getStatusColor(payment.status);

    return (
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: colors.blueAccent[500],
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" fontWeight="bold">
            Payment Details
          </Typography>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Payment Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ color: colors.blueAccent[500] }}>
                Payment Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Payment ID</Typography>
                  <Typography variant="body1" fontWeight="bold">{payment.paymentId}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Booking ID</Typography>
                  <Typography variant="body1" fontWeight="bold">#{payment.bookingId}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Transaction ID</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ wordBreak: 'break-all' }}>
                    {payment.transactionId || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Amount & Status */}
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Amount</Typography>
                  <Typography variant="h5" fontWeight="bold" color={colors.blueAccent[500]}>
                    {formatCurrency(payment.amount, payment.currency)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Chip
                    label={statusColors.label}
                    sx={{
                      backgroundColor: statusColors.bg,
                      color: statusColors.color,
                      fontWeight: 'bold'
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Payment Method</Typography>
                  <Typography variant="body1" fontWeight="bold">{payment.paymentMethod}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Currency</Typography>
                  <Typography variant="body1" fontWeight="bold">{payment.currency}</Typography>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Renter Information */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ color: colors.blueAccent[500] }}>
                Renter Information
              </Typography>
              <Box>
                <Typography variant="body2" color="text.secondary">Name</Typography>
                <Typography variant="body1" fontWeight="bold">{payment.renterName}</Typography>
              </Box>
              <Box mt={1}>
                <Typography variant="body2" color="text.secondary">Email</Typography>
                <Typography variant="body1" fontWeight="bold">{payment.renterEmail}</Typography>
              </Box>
              <Box mt={1}>
                <Typography variant="body2" color="text.secondary">Renter ID</Typography>
                <Typography variant="body1" fontWeight="bold">{payment.renterId}</Typography>
              </Box>
            </Grid>

            {/* Seller Information */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ color: colors.blueAccent[500] }}>
                Seller Information
              </Typography>
              <Box>
                <Typography variant="body2" color="text.secondary">Name</Typography>
                <Typography variant="body1" fontWeight="bold">{payment.sellerName}</Typography>
              </Box>
              <Box mt={1}>
                <Typography variant="body2" color="text.secondary">Email</Typography>
                <Typography variant="body1" fontWeight="bold">{payment.sellerEmail}</Typography>
              </Box>
              <Box mt={1}>
                <Typography variant="body2" color="text.secondary">Seller ID</Typography>
                <Typography variant="body1" fontWeight="bold">{payment.sellerId}</Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Vehicle Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ color: colors.blueAccent[500] }}>
                Vehicle Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Vehicle ID</Typography>
                  <Typography variant="body1" fontWeight="bold">{payment.vehicleId}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Description</Typography>
                  <Typography variant="body1" fontWeight="bold">{payment.description}</Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button 
            onClick={onClose}
            variant="outlined"
            sx={{
              borderColor: colors.blueAccent[500],
              color: colors.blueAccent[500],
              '&:hover': {
                backgroundColor: alpha(colors.blueAccent[500], 0.1),
              }
            }}
          >
            Close
          </Button>
          
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={adminPageSx}>
      {/* Header with inline stats */}
      <Box 
        mb={2}
        p={{ xs: 2, md: 2.5 }}
        borderRadius={2}
        sx={{
          background: `linear-gradient(90deg, #22a3f1 0%, #67baf0 100%)`,
          boxShadow: `0 4px 12px rgba(0, 0, 0, 0.1)`,
          border: `1px solid rgba(255, 255, 255, 0.3)`
        }}
      >
        <Typography 
          variant="h3" 
          fontWeight="bold" 
          color="white"
          sx={{ mb: 0.5, fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" } }}
        >
          Payment Management
        </Typography>
        
        <Box display="flex" flexDirection="column" gap={1}>
          <Typography variant="body1" color="white">
            Track and manage all payment transactions in the system
          </Typography>
          
          {/* Inline stats */}
          <Stack direction="row" spacing={2} mt={1} flexWrap="wrap" gap={1}>
            <Chip
              icon={<PaymentIcon sx={{ fontSize: 14 }} />}
              label={`Total: ${stats.total}`}
              size="small"
              sx={{
                fontWeight: "bold",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                color: "white",
                px: 1,
                '& .MuiChip-icon': {
                  color: "white"
                }
              }}
            />
            <Chip
              icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
              label={`Paid: ${stats.paid}`}
              size="small"
              sx={{
                fontWeight: "bold",
                backgroundColor: "rgba(76, 175, 80, 0.3)",
                color: "white",
                px: 1,
                '& .MuiChip-icon': {
                  color: "white"
                }
              }}
            />
            <Chip
              icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
              label={`Succeeded: ${stats.succeeded}`}
              size="small"
              sx={{
                fontWeight: "bold",
                backgroundColor: "rgba(33, 150, 243, 0.3)",
                color: "white",
                px: 1,
                '& .MuiChip-icon': {
                  color: "white"
                }
              }}
            />
            <Chip
              icon={<CreditCardIcon sx={{ fontSize: 14 }} />}
              label={`Failed: ${stats.failed}`}
              size="small"
              sx={{
                fontWeight: "bold",
                backgroundColor: "rgba(244, 67, 54, 0.3)",
                color: "white",
                px: 1,
                '& .MuiChip-icon': {
                  color: "white"
                }
              }}
            />
          </Stack>
        </Box>
      </Box>

      <Paper
        elevation={3}
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: "#ffffff",
          boxShadow: `0 10px 30px rgba(0, 0, 0, 0.05)`,
          border: `1px solid #e0e0e0`
        }}
      >
        <Box
          sx={{
            ...adminDataGridBoxSx,
            overflowX: "auto",
            "& .MuiDataGrid-root": {
              border: "none",
              fontSize: "13px",
              color: "#333333",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: `1px solid #e0e0e0`,
              padding: '12px',
              fontSize: "13px",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#22a3f1",
              borderBottom: "none",
              fontSize: "14px",
              fontWeight: "bold",
              padding: '0 12px',
              color: "white",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: "#ffffff",
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              backgroundColor: "rgba(34, 163, 241, 0.05)",
              padding: '6px 12px',
              color: "#333333",
            },
            "& .MuiDataGrid-row": {
              '&:hover': {
                backgroundColor: "rgba(34, 163, 241, 0.1)",
              },
            },
            "& .MuiTablePagination-root": {
              color: "#333333",
            },
            "& .MuiIconButton-root": {
              color: "#22a3f1",
            },
            "& .MuiCheckbox-root": {
              color: "#22a3f1",
            },
            "& .MuiSvgIcon-root": {
              "&.MuiSelect-icon": {
                color: "#757575",
              },
            },
            "& .MuiMenuItem-root": {
              color: "#333333",
            },
          }}
        >
          <DataGrid
            rows={data}
            columns={columns}
            columnVisibilityModel={{
              paymentId: !isMobile,
              paymentMethod: !isMobile,
            }}
            components={{ 
              Toolbar: CustomToolbar,
              LoadingOverlay: LinearProgress
            }}
            loading={loading}
            disableSelectionOnClick
            rowHeight={70}
            pageSize={8}
            rowsPerPageOptions={[8, 16, 32]}
            sx={{
              '& .MuiDataGrid-columnSeparator': {
                display: 'none',
              },
            }}
          />
        </Box>
      </Paper>

      {/* Payment Details Dialog */}
      <PaymentDetailsDialog 
        payment={selectedPayment}
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
      />
    </Box>
  );
};

export default PaymentTable;